import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Brain, Check, ThumbsDown, ThumbsUp, X } from "lucide-react";

const TAG = /\[\[RETENIR:\s*(perso|ge)\s*\|\s*(?:(consigne|correction)\s*\|\s*)?([\s\S]*?)\]\]/gi;

export function stripRetenir(text) {
  const propositions = [];
  const texte = (text || "").replace(TAG, (_, portee, type, t) => {
    propositions.push({ portee: portee.toLowerCase(), source: (type || "consigne").toLowerCase(), texte: t.trim().replace(/[.\s]+$/, "") });
    return "";
  })
    .replace(/\[\[RETENIR:[^\]]*$/i, "").trim();
  return { texte, propositions };
}

function Proposition({ p, testId }) {
  const [etat, setEtat] = useState("");
  const retenir = async (portee) => {
    try {
      const { data } = await api.post("/zoe/memoire", { texte: p.texte, portee, source: p.source });
      setEtat(data.statut === "a_valider" ? "Proposé au GE (validation par un admin ou un gestionnaire)" : portee === "ge" ? "Retenu pour tout le GE" : "Retenu pour vous");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  if (etat === "ignore") return null;
  return (
    <div className="mt-2 rounded-lg border border-[#C5A059]/40 bg-[#C5A059]/5 px-3 py-2 text-xs" data-testid={`${testId}-retenir`}>
      <div className="flex items-start gap-2 text-slate-800"><Brain className="h-3.5 w-3.5 mt-0.5 text-[#A88846] shrink-0" />
        <span>Je retiens : <strong>{p.texte}</strong> ?</span></div>
      {etat ? <div className="mt-1 text-emerald-700 flex items-center gap-1" data-testid={`${testId}-retenir-etat`}><Check className="h-3 w-3" />{etat}</div> : (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <button type="button" onClick={() => retenir("perso")} data-testid={`${testId}-retenir-perso`} className="rounded-full bg-[#17352e] text-white px-2.5 py-1 hover:bg-[#284b40]">Retenir pour moi</button>
          <button type="button" onClick={() => retenir("ge")} data-testid={`${testId}-retenir-ge`} className="rounded-full border border-[#17352e] text-[#17352e] px-2.5 py-1 hover:bg-[#17352e] hover:text-white">{p.portee === "ge" ? "Retenir pour tout le GE" : "Proposer au GE"}</button>
          <button type="button" onClick={() => setEtat("ignore")} data-testid={`${testId}-retenir-ignorer`} className="rounded-full px-2.5 py-1 text-slate-500 hover:text-slate-900"><X className="h-3 w-3 inline" /> Ignorer</button>
        </div>)}
    </div>
  );
}

export function Apprentissage({ contexte, sessionId, messageId, question, reponse, propositions, testId }) {
  const [note, setNote] = useState(0);
  const [comment, setComment] = useState(null);
  const envoyer = async (n, commentaire) => {
    try {
      await api.post("/zoe/feedback", { contexte, session_id: sessionId || null, message_id: messageId || null, note: n, commentaire: commentaire || null,
        question: (question || "").slice(0, 4000), reponse: (reponse || "").slice(0, 8000) });
      setNote(n);
      toast.success(n === 1 ? "Merci : ZOE s'en inspirera" : "Merci : ZOE évitera ce type de réponse");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const btn = (actif) => `inline-flex items-center rounded p-1 transition-colors ${actif ? "text-[#A88846] bg-[#C5A059]/10" : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"}`;
  return (
    <div data-testid={`${testId}-apprentissage`}>
      {propositions?.map((p, i) => <Proposition key={i} p={p} testId={`${testId}-${i}`} />)}
      <div className="mt-1 flex items-center gap-1">
        <button type="button" title="Bonne réponse" onClick={() => envoyer(1)} className={btn(note === 1)} data-testid={`${testId}-pouce-haut`}><ThumbsUp className="h-3.5 w-3.5" /></button>
        <button type="button" title="Réponse à améliorer" onClick={() => setComment("")} className={btn(note === -1)} data-testid={`${testId}-pouce-bas`}><ThumbsDown className="h-3.5 w-3.5" /></button>
      </div>
      {comment !== null && note !== -1 && (
        <div className="mt-1 flex gap-1.5 items-center">
          <input value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500} placeholder="Qu'est-ce qui n'allait pas ? (facultatif)"
            className="flex-1 h-8 rounded-md border border-slate-200 px-2 text-xs" data-testid={`${testId}-commentaire`} />
          <button type="button" onClick={() => { envoyer(-1, comment); setComment(null); }} className="h-8 rounded-md bg-slate-900 text-white text-xs px-3" data-testid={`${testId}-commentaire-envoyer`}>Envoyer</button>
        </div>)}
    </div>
  );
}
